import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  Button,
  Text,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Avatar,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Center,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { SearchIcon, ViewIcon, DownloadIcon, EditIcon, StarIcon, DeleteIcon } from '@chakra-ui/icons';
import { FiMoreVertical, FiTrash2, FiEdit } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Category, CategoryExplorerFilter, CategoryExplorerResponse } from '../../types/game.ts';
import './CategoryComponents.css';
import { GTMEvents } from '../../utils/gtm.ts';
import CategoryCreator from './CategoryCreator.tsx';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

interface CategoryExplorerProps {
  isOpen: boolean;
  onClose: () => void;
  onCategorySelected?: (category: Category) => void;
  user?: any; // Changé de userToken vers user
  currentUserId?: string;
  mode?: 'explore' | 'manage'; // Mode exploration ou gestion des catégories personnelles
}

const CategoryExplorer: React.FC<CategoryExplorerProps> = ({
  isOpen,
  onClose,
  onCategorySelected,
  user, // Changé de userToken vers user
  currentUserId,
  mode = 'explore'
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  // États
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [categoryToEdit, setCategoryToEdit] = useState<Category | null>(null);
  const [userFavorites, setUserFavorites] = useState<string[]>([]); // IDs des catégories favorites
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'explore' | 'mine' | 'favorites'>(mode === 'manage' ? 'mine' : 'explore');
  const [filter, setFilter] = useState<CategoryExplorerFilter>({
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
    page: 1,
    limit: 12
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      if (user && mode === 'explore') {
        loadUserFavorites();
      }
    }
  }, [isOpen, filter, mode, user, viewMode]);

  // Effet pour forcer la gestion des événements de scroll de la souris
  useEffect(() => {
    if (isOpen && modalBodyRef.current) {
      const modalBody = modalBodyRef.current;
      
      const handleWheel = (e: WheelEvent) => {
        // Empêcher la propagation vers le parent
        e.stopPropagation();
        
        // Scroll manuel si nécessaire
        if (modalBody) {
          modalBody.scrollTop += e.deltaY;
        }
      };

      // Ajouter l'écouteur d'événement wheel
      modalBody.addEventListener('wheel', handleWheel, { passive: false });
      
      // Forcer le focus sur l'élément pour s'assurer qu'il reçoit les événements
      modalBody.focus();
      
      return () => {
        modalBody.removeEventListener('wheel', handleWheel);
      };
    }
  }, [isOpen]);

  const loadUserFavorites = async () => {
    if (!user) return;
    
    setFavoritesLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/favorites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Les favoris retournent category_key comme 'id'
        const favoriteIds = data.favorites?.map((fav: any) => fav.id) || [];
        console.log('🔍 [CategoryExplorer] Loaded user favorites:', favoriteIds);
        setUserFavorites(favoriteIds);
      }
    } catch (error) {
      console.error('❌ [CategoryExplorer] Error loading user favorites:', error);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_API_URL}/api/categories/explore`;
      let userIdForCheck = currentUserId;
      
      // Obtenir le userId depuis Firebase si pas défini
      if (!userIdForCheck && user) {
        userIdForCheck = user.uid;
      }
      
      console.log('🔍 [CategoryExplorer] loadCategories - viewMode:', viewMode, 'currentUserId:', currentUserId, 'user.uid:', user?.uid, 'userIdForCheck:', userIdForCheck);
      
      if (viewMode === 'mine' && userIdForCheck) {
        url = `${process.env.REACT_APP_API_URL}/api/categories/my-categories`;
        console.log('✅ [CategoryExplorer] Mode MES CRÉATIONS - URL:', url);
      } else if (viewMode === 'favorites' && user) {
        url = `${process.env.REACT_APP_API_URL}/api/user/favorites`;
        console.log('✅ [CategoryExplorer] Mode FAVORIS - URL:', url);
      } else {
        console.log('✅ [CategoryExplorer] Mode EXPLORER - URL:', url);
      }

      const params = new URLSearchParams();
      // Ne pas envoyer search/sort/pagination pour favorites car l'API favorites ne les gère pas encore
      if (viewMode !== 'favorites') {
        if (filter.search) params.append('search', filter.search);
        if (filter.sortBy) params.append('sortBy', filter.sortBy);
        if (filter.sortOrder) params.append('sortOrder', filter.sortOrder);
        if (filter.page) params.append('page', filter.page.toString());
        if (filter.limit) params.append('limit', filter.limit.toString());
      }

      console.log('📤 [CategoryExplorer] Paramètres de requête:', {
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
        search: filter.search,
        page: filter.page,
        viewMode,
        url: `${url}?${params.toString()}`
      });

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Obtenir le token Firebase si l'utilisateur est connecté
      if (user) {
        try {
          const token = await user.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        } catch (error) {
          console.error('Erreur lors de la récupération du token:', error);
        }
      }

      const response = await fetch(`${url}?${params}`, { headers });

      if (!response.ok) {
        throw new Error('Failed to load categories');
      }

      const data = await response.json();
      
      if (viewMode === 'favorites') {
        // API favorites retourne { favorites: [...] }
        let favs = data.favorites || [];
        
        // Tri côté client pour les favoris
        favs = favs.sort((a: Category, b: Category) => {
          if (filter.sortBy === 'name') {
            const comparison = a.name.localeCompare(b.name);
            return filter.sortOrder === 'asc' ? comparison : -comparison;
          } else if (filter.sortBy === 'question_count') {
            const countA = a.question_count || 0;
            const countB = b.question_count || 0;
            return filter.sortOrder === 'desc' ? countB - countA : countA - countB;
          } else if (filter.sortBy === 'created_at') {
            const dateA = new Date(a.created_at || 0).getTime();
            const dateB = new Date(b.created_at || 0).getTime();
            return filter.sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
          }
          return 0;
        });
        
        // Recherche côté client pour les favoris
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          favs = favs.filter((cat: Category) => 
            cat.name.toLowerCase().includes(searchLower) ||
            (cat.description && cat.description.toLowerCase().includes(searchLower)) ||
            (cat.creator_name && cat.creator_name.toLowerCase().includes(searchLower))
          );
        }
        
        setCategories(favs);
        setTotalPages(1);
        setTotal(favs.length);
      } else {
        // API explore et my-categories retournent { categories, total, totalPages }
        let resultCategories = data.categories || [];
        
        // Double sécurité : filtrer côté client pour "Mes Créations"
        if (viewMode === 'mine' && userIdForCheck) {
          console.log('🔒 [CategoryExplorer] Filtre sécurité client - userIdForCheck:', userIdForCheck);
          const beforeFilter = resultCategories.length;
          resultCategories = resultCategories.filter((cat: Category) => {
            const matches = cat.creator_id === userIdForCheck;
            if (!matches) {
              console.warn('⚠️ [CategoryExplorer] Catégorie filtrée:', {
                name: cat.name,
                creator_id: cat.creator_id,
                expected: userIdForCheck
              });
            }
            return matches;
          });
          console.log(`🔒 [CategoryExplorer] Filtrage: ${beforeFilter} -> ${resultCategories.length} catégories`);
        }
        
        setCategories(resultCategories);
        setTotalPages(data.totalPages || 1);
        // Utiliser data.total du serveur même pour "mine" car il contient le total réel, pas juste la page
        setTotal(data.total || 0);
      }

      // Tracker l'événement
      GTMEvents.pageView(viewMode === 'mine' ? 'my_categories' : viewMode === 'favorites' ? 'favorites' : 'explore_categories');

    } catch (error) {
      console.error('❌ [CategoryExplorer] Error loading categories:', error);
      setCategories([]); // S'assurer que categories reste un tableau vide
      setTotalPages(1);
      setTotal(0);
      toast({
        title: t('categoryExplorer.errors.loadFailed'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category);
    onDetailOpen();
    
    GTMEvents.categorySelected(category.name, 'explorer');
  };

  const handleDeleteCategory = async (category: Category) => {
    if (!user) {
      console.error('❌ [CategoryExplorer] Pas d\'utilisateur connecté');
      return;
    }
    
    const userIdForCheck = currentUserId || user.uid;
    
    if (category.creator_id !== userIdForCheck) {
      console.error('❌ [CategoryExplorer] Catégorie n\'appartient pas à l\'utilisateur', {
        category_creator: category.creator_id,
        user_id: userIdForCheck
      });
      return;
    }

    console.log('🗑️ [CategoryExplorer] Début suppression:', category.name);

    try {
      const token = await user.getIdToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories/${category.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete category');
      }

      console.log('✅ [CategoryExplorer] Catégorie supprimée avec succès');

      toast({
        title: '🎉 Catégorie supprimée',
        description: `La catégorie "${category.name}" a été supprimée définitivement`,
        status: 'success',
        duration: 3000,
      });

      // Recharger la liste
      loadCategories();
      onDeleteClose();
      setCategoryToDelete(null);

    } catch (error) {
      console.error('❌ [CategoryExplorer] Error deleting category:', error);
      toast({
        title: 'Erreur de suppression',
        description: error instanceof Error ? error.message : 'Impossible de supprimer la catégorie',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleEditCategory = (category: Category) => {
    setCategoryToEdit(category);
    onEditOpen();
  };

  const handleCategoryEdited = () => {
    loadCategories(); // Recharger la liste après modification
    onEditClose();
    setCategoryToEdit(null);
    toast({
      title: t('categoryExplorer.success.categoryEdited'),
      status: 'success',
      duration: 3000,
    });
  };

  const handleAddToFavorites = async (category: Category) => {
    if (!user) {
      toast({
        title: t('categoryExplorer.errors.loginRequired'),
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    console.log('🔍 [CategoryExplorer] Current userFavorites:', userFavorites);
    console.log('🔍 [CategoryExplorer] Category ID:', category.id);
    console.log('🔍 [CategoryExplorer] Is favorite?', userFavorites.includes(category.id));

    const isFavorite = userFavorites.includes(category.id);
    const endpoint = isFavorite ? 'remove' : 'add';
    const method = 'POST';

    try {
      const token = await user.getIdToken();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/favorites/${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ categoryId: category.id }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${endpoint} favorite`);
      }

      // Mettre à jour l'état local des favoris
      if (isFavorite) {
        setUserFavorites(prev => prev.filter(id => id !== category.id));
        toast({
          title: 'Retiré des favoris !',
          description: `${category.name} a été retiré de vos favoris`,
          status: 'info',
          duration: 3000,
        });
      } else {
        setUserFavorites(prev => [...prev, category.id]);
        toast({
          title: 'Ajouté aux favoris !',
          description: `${category.name} a été ajoutée à vos favoris`,
          status: 'success',
          duration: 3000,
        });
      }

      console.log('🔍 [CategoryExplorer] Updated userFavorites:', isFavorite ? 
        userFavorites.filter(id => id !== category.id) : 
        [...userFavorites, category.id]
      );

      // Recharger les favoris pour être sûr de la synchronisation
      await loadUserFavorites();

      onCategorySelected?.(category);

    } catch (error) {
      console.error(`❌ [CategoryExplorer] Error ${endpoint}ing favorite:`, error);
      toast({
        title: 'Erreur',
        description: error instanceof Error ? error.message : `Impossible de ${endpoint === 'add' ? 'ajouter aux' : 'retirer des'} favoris`,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderCategoryCard = (category: Category) => (
    <MotionCard
      key={category.id}
      className="category-explorer-card"
      cursor="pointer"
      onClick={() => handleCategoryClick(category)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
    >
      <CardHeader pb={2} p={{ base: 3, md: 4 }}>
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={1} flex={1}>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="bold" className="category-explorer-text">
                {category.emoji || '📚'} {category.name}
              </Text>
              {category.is_custom && (
                <Badge colorScheme="purple" variant="subtle" fontSize={{ base: 'xs', md: 'sm' }}>
                  Personnalisée
                </Badge>
              )}
              {!category.is_public && (
                <Badge colorScheme="orange" variant="subtle" fontSize={{ base: 'xs', md: 'sm' }}>
                  Privée
                </Badge>
              )}
            </HStack>
            {category.creator_name && (
              <Text fontSize={{ base: 'xs', md: 'sm' }} className="category-explorer-text-muted">
                Par {category.creator_name}
              </Text>
            )}
          </VStack>
          
          {viewMode === 'mine' && category.creator_id === currentUserId && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<FiMoreVertical />}
                size="sm"
                variant="ghost"
                color="var(--texte-secondaire)"
                onClick={(e) => e.stopPropagation()}
              />
              <MenuList 
                bg="var(--nuit-profonde)" 
                border="1px solid var(--gris-bleute)"
                color="var(--texte-clair)"
              >
                <MenuItem
                  icon={<FiEdit />}
                  color="var(--texte-clair)"
                  bg="transparent"
                  _hover={{ bg: 'var(--gris-bleute)', color: 'white' }}
                  _focus={{ bg: 'var(--gris-bleute)', color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditCategory(category);
                  }}
                >
                  Modifier
                </MenuItem>
                <MenuItem
                  icon={<FiTrash2 />}
                  color="red.400"
                  bg="transparent"
                  _hover={{ bg: 'red.600', color: 'white' }}
                  _focus={{ bg: 'red.600', color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCategoryToDelete(category);
                    onDeleteOpen();
                  }}
                >
                  Supprimer
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </HStack>
      </CardHeader>
      
      <CardBody pt={0} p={{ base: 3, md: 4 }}>
        <VStack align="start" spacing={{ base: 2, md: 3 }}>
          <Text fontSize={{ base: 'xs', md: 'sm' }} color="var(--texte-secondaire)" noOfLines={2}>
            {category.description || 'Aucune description'}
          </Text>
          <HStack spacing={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }} color="var(--texte-secondaire)" flexWrap="wrap">
            <Text>🎯 {category.question_count || 0} questions</Text>
            {category.created_at && (
              <Text display={{ base: 'none', sm: 'block' }}>📅 {new Date(category.created_at).toLocaleDateString()}</Text>
            )}
          </HStack>
        </VStack>
      </CardBody>
    </MotionCard>
  );

  const renderCategoryDetail = () => (
    <Modal isOpen={isDetailOpen} onClose={onDetailClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent className="category-explorer-modal">
        <ModalHeader
          bg={selectedCategory?.color}
          color="white"
          borderTopRadius="md"
          className="category-explorer-header"
        >
          <VStack align="start" spacing={2}>
            <Text fontSize="xl" fontWeight="bold">
              {selectedCategory?.name}
            </Text>
            <HStack spacing={2}>
              {selectedCategory?.is_custom && (
                <Badge colorScheme="green" variant="solid">
                  {t('categoryExplorer.badges.custom')}
                </Badge>
              )}
              {!selectedCategory?.is_public && (
                <Badge colorScheme="orange" variant="solid">
                  {t('categoryExplorer.badges.private')}
                </Badge>
              )}
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton color="white" />
        
        <ModalBody p={6} className="category-explorer-body category-explorer-scrollbar category-modal-body-scrollable">
          <VStack spacing={6} align="stretch">
            {selectedCategory?.description && (
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                  {t('categoryExplorer.description')}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {selectedCategory.description}
                </Text>
              </Box>
            )}
            
            <Divider />
            
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <Stat>
                <StatLabel>{t('categoryExplorer.statistics.questions')}</StatLabel>
                <StatNumber>{selectedCategory?.question_count || 0}</StatNumber>
              </Stat>
              
              {selectedCategory?.creator_name && (
                <Stat>
                  <StatLabel>{t('categoryExplorer.statistics.creator')}</StatLabel>
                  <HStack>
                    <Avatar size="sm" name={selectedCategory.creator_name} />
                    <Text fontSize="md" fontWeight="semibold">
                      {selectedCategory.creator_name}
                    </Text>
                  </HStack>
                </Stat>
              )}
            </Grid>
            
            {selectedCategory?.created_at && (
              <Box>
                <Text fontSize="sm" color="gray.500">
                  {t('categoryExplorer.createdAt', { 
                    date: formatDate(selectedCategory.created_at) 
                  })}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        
        <ModalFooter>
          <HStack spacing={3}>
            <Button 
              onClick={onDetailClose}
              variant="solid"
              colorScheme="gray"
              color="white"
              bg="gray.600"
              _hover={{ bg: "gray.700" }}
            >
              {t('categoryExplorer.buttons.close')}
            </Button>
            
            {/* Bouton d'édition si c'est la catégorie de l'utilisateur */}
            {selectedCategory && user && selectedCategory.creator_id === user.uid && (
              <>
                <Button
                  colorScheme="purple"
                  variant="solid"
                  leftIcon={<EditIcon />}
                  color="white"
                  bg="purple.500"
                  _hover={{ bg: "purple.600" }}
                  onClick={() => {
                    setCategoryToEdit(selectedCategory);
                    onDetailClose();
                    onEditOpen();
                  }}
                >
                  ✏️ Modifier
                </Button>
                
                <Button
                  colorScheme="red"
                  variant="solid"
                  leftIcon={<DeleteIcon />}
                  color="white"
                  bg="red.500"
                  _hover={{ 
                    bg: "red.600",
                    transform: "scale(1.05)",
                    boxShadow: "0 0 20px rgba(220, 38, 38, 0.5)"
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                    setCategoryToDelete(selectedCategory);
                    onDetailClose();
                    onDeleteOpen();
                  }}
                >
                  🗑️ Supprimer
                </Button>
              </>
            )}
            
            {mode === 'explore' && selectedCategory && (
              <Button
                colorScheme={userFavorites.includes(selectedCategory.id) ? "red" : "yellow"}
                leftIcon={<StarIcon />}
                onClick={() => {
                  handleAddToFavorites(selectedCategory);
                  onDetailClose();
                }}
              >
                {userFavorites.includes(selectedCategory.id) ? "🗑️ Retirer des favoris" : "⭐ Ajouter aux favoris"}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const renderDeleteConfirmation = () => (
    <AlertDialog
      isOpen={isDeleteOpen}
      leastDestructiveRef={cancelRef}
      onClose={onDeleteClose}
      isCentered
      motionPreset="slideInBottom"
    >
      <AlertDialogOverlay 
        bg="blackAlpha.800"
        backdropFilter="blur(4px)"
      />
      <AlertDialogContent
        bg="linear-gradient(135deg, #1a0000 0%, #330000 50%, #1a0000 100%)"
        border="2px solid"
        borderColor="red.600"
        boxShadow="0 0 40px rgba(220, 38, 38, 0.4), inset 0 0 20px rgba(220, 38, 38, 0.1)"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(220, 38, 38, 0.03) 10px, rgba(220, 38, 38, 0.03) 20px)",
          pointerEvents: "none"
        }}
      >
        <AlertDialogHeader 
          fontSize="2xl" 
          fontWeight="bold"
          color="red.400"
          textAlign="center"
          py={6}
          position="relative"
        >
          <VStack spacing={2}>
            <Box
              fontSize="4xl"
              animation="pulse 2s ease-in-out infinite"
              sx={{
                "@keyframes pulse": {
                  "0%, 100%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(1.1)", opacity: 0.8 }
                }
              }}
            >
              ⚠️
            </Box>
            <Text>ZONE DE DANGER</Text>
            <Text fontSize="sm" color="red.300" fontWeight="normal">
              Action irréversible
            </Text>
          </VStack>
        </AlertDialogHeader>

        <AlertDialogBody color="gray.200" py={6} px={8} position="relative">
          <VStack spacing={4} align="stretch">
            <Box 
              bg="red.900" 
              p={4} 
              borderRadius="md" 
              border="1px solid"
              borderColor="red.600"
              position="relative"
            >
              <HStack spacing={3} mb={2}>
                <Box fontSize="2xl">💀</Box>
                <Text fontWeight="bold" color="red.200" fontSize="lg">
                  Suppression définitive
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.300" lineHeight="tall">
                Vous êtes sur le point de <Text as="span" fontWeight="bold" color="red.300">supprimer définitivement</Text> la catégorie :
              </Text>
              <Text 
                fontSize="xl" 
                fontWeight="bold" 
                color="white" 
                mt={2} 
                textAlign="center"
                bg="blackAlpha.500"
                py={2}
                px={3}
                borderRadius="md"
              >
                {categoryToDelete?.emoji} {categoryToDelete?.name}
              </Text>
            </Box>
            
            <Box 
              bg="orange.900" 
              p={3} 
              borderRadius="md"
              border="1px dashed"
              borderColor="orange.600"
            >
              <HStack spacing={2} mb={1}>
                <Text fontSize="lg">🔥</Text>
                <Text fontWeight="semibold" color="orange.200" fontSize="sm">
                  Conséquences :
                </Text>
              </HStack>
              <VStack align="start" spacing={1} pl={6}>
                <Text fontSize="xs" color="gray.300">
                  • Toutes les <Text as="span" fontWeight="bold">{categoryToDelete?.question_count || 0} questions</Text> seront supprimées
                </Text>
                <Text fontSize="xs" color="gray.300">
                  • Cette action <Text as="span" fontWeight="bold" color="red.300">ne peut PAS être annulée</Text>
                </Text>
                <Text fontSize="xs" color="gray.300">
                  • Les données seront <Text as="span" fontWeight="bold">perdues à jamais</Text>
                </Text>
              </VStack>
            </Box>
          </VStack>
        </AlertDialogBody>

        <AlertDialogFooter 
          justifyContent="center" 
          gap={4}
          pb={6}
          position="relative"
        >
          <Button 
            ref={cancelRef} 
            onClick={onDeleteClose}
            size="lg"
            variant="solid"
            bg="gray.700"
            color="white"
            _hover={{ bg: "gray.600", transform: "translateY(-2px)" }}
            _active={{ transform: "translateY(0)" }}
            transition="all 0.2s"
            leftIcon={<Box>✋</Box>}
            px={8}
          >
            Non, annuler
          </Button>
          <Button
            colorScheme="red"
            onClick={() => categoryToDelete && handleDeleteCategory(categoryToDelete)}
            size="lg"
            bg="red.600"
            color="white"
            _hover={{ 
              bg: "red.700",
              transform: "translateY(-2px)",
              boxShadow: "0 0 30px rgba(220, 38, 38, 0.6)"
            }}
            _active={{ transform: "translateY(0)" }}
            transition="all 0.2s"
            leftIcon={<Box animation="shake 0.5s ease-in-out infinite">💣</Box>}
            px={8}
            sx={{
              "@keyframes shake": {
                "0%, 100%": { transform: "rotate(0deg)" },
                "25%": { transform: "rotate(-5deg)" },
                "75%": { transform: "rotate(5deg)" }
              }
            }}
          >
            Oui, supprimer définitivement
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <>
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        size={{ base: 'full', md: '6xl' }}
        scrollBehavior="inside"
      >
        <MotionBox
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
        >
          <ModalOverlay 
            className="category-modal-overlay category-explorer-overlay" 
          />
          <ModalContent 
            maxH={{ base: '100vh', md: '90vh' }}
            className="category-explorer-modal category-modal-content category-explorer-content"
            mx={{ base: 0, md: 'auto' }}
            my={{ base: 0, md: 'auto' }}
          >
            <ModalHeader className="category-explorer-header" p={{ base: 3, md: 6 }}>
              <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                {/* Onglets créatifs */}
                <HStack spacing={0} className="category-tabs">
                  <Button
                    flex={1}
                    onClick={() => {
                      console.log('🔘 [CategoryExplorer] Clic sur onglet EXPLORER');
                      setViewMode('explore');
                      setFilter(prev => ({ ...prev, page: 1 }));
                    }}
                    bg={viewMode === 'explore' ? 'var(--bleu-electrique)' : 'transparent'}
                    color={viewMode === 'explore' ? 'white' : 'var(--texte-secondaire)'}
                    borderRadius="15px 0 0 15px"
                    border="2px solid"
                    borderColor={viewMode === 'explore' ? 'var(--bleu-electrique)' : 'var(--gris-bleute)'}
                    _hover={{
                      bg: viewMode === 'explore' ? 'var(--bleu-electrique)' : 'var(--gris-bleute)',
                      color: 'white'
                    }}
                    transition="all 0.3s"
                    position="relative"
                    overflow="hidden"
                    px={{ base: 2, md: 4 }}
                    minH={{ base: '45px', md: '50px' }}
                  >
                    <HStack spacing={{ base: 1, md: 2 }}>
                      <Text fontSize={{ base: 'xl', md: 'lg' }}>🌍</Text>
                      <Text fontWeight="bold" display={{ base: 'none', sm: 'block' }} fontSize={{ base: 'xs', md: 'md' }}>Explorer</Text>
                    </HStack>
                    {viewMode === 'explore' && (
                      <Box
                        position="absolute"
                        bottom="0"
                        left="0"
                        right="0"
                        height="3px"
                        bg="white"
                        animation="shimmer 2s infinite"
                      />
                    )}
                  </Button>
                  
                  {user && (
                    <>
                      <Button
                        flex={1}
                        onClick={() => {
                          console.log('🔘 [CategoryExplorer] Clic sur onglet MES CRÉATIONS');
                          setViewMode('mine');
                          setFilter(prev => ({ ...prev, page: 1 }));
                        }}
                        bg={viewMode === 'mine' ? 'var(--violet-profond)' : 'transparent'}
                        color={viewMode === 'mine' ? 'white' : 'var(--texte-secondaire)'}
                        borderRadius="0"
                        border="2px solid"
                        borderColor={viewMode === 'mine' ? 'var(--violet-profond)' : 'var(--gris-bleute)'}
                        borderLeft="none"
                        _hover={{
                          bg: viewMode === 'mine' ? 'var(--violet-profond)' : 'var(--gris-bleute)',
                          color: 'white'
                        }}
                        transition="all 0.3s"
                        position="relative"
                        overflow="hidden"
                        px={{ base: 2, md: 4 }}
                        minH={{ base: '45px', md: '50px' }}
                      >
                        <HStack spacing={{ base: 1, md: 2 }}>
                          <Text fontSize={{ base: 'xl', md: 'lg' }}>⚙️</Text>
                          <Text fontWeight="bold" display={{ base: 'none', sm: 'block' }} fontSize={{ base: 'xs', md: 'md' }}>Mes Créations</Text>
                        </HStack>
                        {viewMode === 'mine' && (
                          <Box
                            position="absolute"
                            bottom="0"
                            left="0"
                            right="0"
                            height="3px"
                            bg="white"
                            animation="shimmer 2s infinite"
                          />
                        )}
                      </Button>
                      
                      <Button
                        flex={1}
                        onClick={() => {
                          console.log('🔘 [CategoryExplorer] Clic sur onglet FAVORIS');
                          setViewMode('favorites');
                          setFilter(prev => ({ ...prev, page: 1 }));
                        }}
                        bg={viewMode === 'favorites' ? 'var(--or-dore)' : 'transparent'}
                        color={viewMode === 'favorites' ? 'white' : 'var(--texte-secondaire)'}
                        borderRadius="0 15px 15px 0"
                        border="2px solid"
                        borderColor={viewMode === 'favorites' ? 'var(--or-dore)' : 'var(--gris-bleute)'}
                        borderLeft="none"
                        _hover={{
                          bg: viewMode === 'favorites' ? 'var(--or-dore)' : 'var(--gris-bleute)',
                          color: 'white'
                        }}
                        transition="all 0.3s"
                        position="relative"
                        overflow="hidden"
                        px={{ base: 2, md: 4 }}
                        minH={{ base: '45px', md: '50px' }}
                      >
                        <HStack spacing={{ base: 1, md: 2 }}>
                          <Text fontSize={{ base: 'xl', md: 'lg' }}>⭐</Text>
                          <Text fontWeight="bold" display={{ base: 'none', sm: 'block' }} fontSize={{ base: 'xs', md: 'md' }}>Favoris</Text>
                        </HStack>
                        {viewMode === 'favorites' && (
                          <Box
                            position="absolute"
                            bottom="0"
                            left="0"
                            right="0"
                            height="3px"
                            bg="white"
                            animation="shimmer 2s infinite"
                          />
                        )}
                      </Button>
                    </>
                  )}
                </HStack>
                
                <VStack spacing={{ base: 2, md: 0 }} align="stretch">
                  <HStack justify="space-between" flexWrap="wrap" gap={2}>
                    <Text fontSize={{ base: 'xs', md: 'sm' }} className="category-explorer-text-muted" flex="1" minW="150px">
                      {viewMode === 'mine' && '✨ Gérez vos catégories personnalisées'}
                      {viewMode === 'explore' && '🔍 Découvrez des catégories créées par la communauté'}
                      {viewMode === 'favorites' && '💫 Vos catégories préférées'}
                    </Text>
                    <Badge 
                      colorScheme={viewMode === 'mine' ? 'purple' : viewMode === 'favorites' ? 'yellow' : 'blue'} 
                      fontSize={{ base: 'xs', md: 'sm' }}
                      flexShrink={0}
                    >
                      {total} {t('categoryExplorer.categoriesFound')}
                    </Badge>
                  </HStack>
                </VStack>
                
                <VStack spacing={{ base: 2, md: 4 }} align="stretch">
                  <InputGroup w="full">
                    <InputLeftElement>
                      <SearchIcon />
                    </InputLeftElement>
                    <Input
                      placeholder={t('categoryExplorer.searchPlaceholder')}
                      value={filter.search}
                      onChange={(e) => setFilter(prev => ({ 
                        ...prev, 
                        search: e.target.value,
                        page: 1 
                      }))}
                      size={{ base: 'sm', md: 'md' }}
                    />
                  </InputGroup>

                  {/* Système de tri visuel avec badges cliquables */}
                  <VStack align="stretch" spacing={2}>
                    <Text fontSize={{ base: 'xs', md: 'sm' }} color="var(--texte-secondaire)" fontWeight="bold">
                      🎯 Trier par:
                    </Text>
                    <Wrap spacing={2}>
                      {/* Tri par date */}
                      <WrapItem>
                        <Button
                          size={{ base: 'xs', md: 'sm' }}
                          variant={filter.sortBy === 'created_at' && filter.sortOrder === 'desc' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => {
                            console.log('🔄 Click: Plus récentes');
                            setFilter(prev => ({ ...prev, sortBy: 'created_at', sortOrder: 'desc', page: 1 }));
                          }}
                          leftIcon={<Text>🆕</Text>}
                          _hover={{ transform: 'scale(1.05)' }}
                          transition="all 0.2s"
                        >
                          Plus récentes
                        </Button>
                      </WrapItem>
                      
                      <WrapItem>
                        <Button
                          size={{ base: 'xs', md: 'sm' }}
                          variant={filter.sortBy === 'created_at' && filter.sortOrder === 'asc' ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => {
                            console.log('🔄 Click: Plus anciennes');
                            setFilter(prev => ({ ...prev, sortBy: 'created_at', sortOrder: 'asc', page: 1 }));
                          }}
                          leftIcon={<Text>📜</Text>}
                          _hover={{ transform: 'scale(1.05)' }}
                          transition="all 0.2s"
                        >
                          Plus anciennes
                        </Button>
                      </WrapItem>

                      {/* Tri par nom */}
                      <WrapItem>
                        <Button
                          size={{ base: 'xs', md: 'sm' }}
                          variant={filter.sortBy === 'name' && filter.sortOrder === 'asc' ? 'solid' : 'outline'}
                          colorScheme="purple"
                          onClick={() => {
                            console.log('🔄 Click: A → Z');
                            setFilter(prev => ({ ...prev, sortBy: 'name', sortOrder: 'asc', page: 1 }));
                          }}
                          leftIcon={<Text>🔤</Text>}
                          _hover={{ transform: 'scale(1.05)' }}
                          transition="all 0.2s"
                        >
                          A → Z
                        </Button>
                      </WrapItem>
                      
                      <WrapItem>
                        <Button
                          size={{ base: 'xs', md: 'sm' }}
                          variant={filter.sortBy === 'name' && filter.sortOrder === 'desc' ? 'solid' : 'outline'}
                          colorScheme="purple"
                          onClick={() => {
                            console.log('🔄 Click: Z → A');
                            setFilter(prev => ({ ...prev, sortBy: 'name', sortOrder: 'desc', page: 1 }));
                          }}
                          leftIcon={<Text>🔠</Text>}
                          _hover={{ transform: 'scale(1.05)' }}
                          transition="all 0.2s"
                        >
                          Z → A
                        </Button>
                      </WrapItem>

                      {/* Tri par questions */}
                      <WrapItem>
                        <Button
                          size={{ base: 'xs', md: 'sm' }}
                          variant={filter.sortBy === 'question_count' && filter.sortOrder === 'desc' ? 'solid' : 'outline'}
                          colorScheme="green"
                          onClick={() => {
                            console.log('🔄 Click: Plus de questions');
                            setFilter(prev => ({ ...prev, sortBy: 'question_count', sortOrder: 'desc', page: 1 }));
                          }}
                          leftIcon={<Text>🎯</Text>}
                          _hover={{ transform: 'scale(1.05)' }}
                          transition="all 0.2s"
                        >
                          Plus de questions
                        </Button>
                      </WrapItem>
                    </Wrap>
                  </VStack>
                </VStack>
              </VStack>
            </ModalHeader>
            <ModalCloseButton className="category-explorer-text" top={{ base: 2, md: 4 }} right={{ base: 2, md: 4 }} />
            
            <ModalBody 
              ref={modalBodyRef}
              overflowY="auto" 
              className="category-explorer-body category-explorer-scrollbar category-modal-body-scrollable"
              tabIndex={-1}
              style={{ outline: 'none' }}
              onWheel={(e) => {
                // Forcer la gestion du scroll de la souris
                e.stopPropagation();
              }}
              p={{ base: 3, md: 6 }}
            >
              {loading ? (
                <Center py={20}>
                  <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" />
                    <Text>{t('categoryExplorer.loading')}</Text>
                  </VStack>
                </Center>
              ) : (() => {
                // Les catégories sont déjà filtrées par l'API selon le viewMode
                const displayedCategories = categories;
                
                return displayedCategories.length === 0 ? (
                  <Center py={20}>
                    <VStack spacing={4}>
                      <Text fontSize="lg" color="gray.500">
                        {viewMode === 'mine' && 'Vous n\'avez créé aucune catégorie'}
                        {viewMode === 'explore' && 'Aucune catégorie trouvée'}
                        {viewMode === 'favorites' && 'Aucun favori pour le moment'}
                      </Text>
                      {filter.search && (
                        <Button
                          variant="ghost"
                          onClick={() => setFilter(prev => ({ ...prev, search: '', page: 1 }))}
                        >
                          {t('categoryExplorer.clearSearch')}
                        </Button>
                      )}
                    </VStack>
                  </Center>
                ) : (
                <VStack spacing={{ base: 4, md: 6 }}>
                  <Grid 
                    templateColumns={{ 
                      base: '1fr', 
                      sm: 'repeat(auto-fill, minmax(250px, 1fr))',
                      md: 'repeat(auto-fill, minmax(300px, 1fr))' 
                    }} 
                    gap={{ base: 3, md: 6 }} 
                    w="full"
                  >
                    {displayedCategories.map(renderCategoryCard)}
                  </Grid>
                  
                  {totalPages > 1 && viewMode !== 'favorites' && (
                    <HStack spacing={2} flexWrap="wrap" justify="center">
                      <Button
                        size={{ base: 'xs', md: 'sm' }}
                        isDisabled={filter.page === 1}
                        onClick={() => setFilter(prev => ({ ...prev, page: (prev.page || 1) - 1 }))}
                      >
                        {t('categoryExplorer.pagination.previous')}
                      </Button>
                      
                      <Text fontSize={{ base: 'xs', md: 'sm' }} px={{ base: 2, md: 4 }}>
                        {t('categoryExplorer.pagination.pageOf', { 
                          current: filter.page || 1, 
                          total: totalPages 
                        })}
                      </Text>
                      
                      <Button
                        size={{ base: 'xs', md: 'sm' }}
                        isDisabled={filter.page === totalPages}
                        onClick={() => setFilter(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                      >
                        {t('categoryExplorer.pagination.next')}
                      </Button>
                    </HStack>
                  )}
                </VStack>
                );
              })()}
            </ModalBody>
          </ModalContent>
        </MotionBox>
      </Modal>

      {renderCategoryDetail()}
      {renderDeleteConfirmation()}
      
      {/* Modal d'édition de catégorie */}
      {categoryToEdit && (
        <CategoryCreator
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose();
            setCategoryToEdit(null);
          }}
          onCategoryCreated={handleCategoryEdited}
          user={user}
          editCategory={categoryToEdit}
          onCloseParentModal={onClose} // Fermer CategoryExplorer quand CategoryCreator s'ouvre
        />
      )}
    </>
  );
};

export default CategoryExplorer;
